import type { ParsedLocation } from "@tanstack/react-router"

import { redirect } from "@tanstack/react-router"

import type { AppRouterContext } from "./router-context"

import { isAuthQueryFresh, resolveFreshAuth } from "./auth-query"

type AuthGuardContext = {
  context: AppRouterContext
  location: ParsedLocation
}

export const sanitizeRedirectTarget = (value: unknown): string | undefined => {
  if (typeof value !== "string") {
    return undefined
  }

  if (!value.startsWith("/") || value.startsWith("//")) {
    return undefined
  }

  return value
}

const buildRedirectTarget = (location: ParsedLocation) => {
  const search = location.href.includes("?")
    ? location.href.slice(location.href.indexOf("?"))
    : ""

  return `${location.pathname}${search}${location.hash}`
}

export const getResolvedAuth = async ({
  context,
}: Pick<AuthGuardContext, "context">) => {
  const isFresh = isAuthQueryFresh(context.queryClient)

  if (context.auth.status === "authenticated" && isFresh) {
    return context.auth.user
  }

  if (context.auth.status === "guest" && isFresh) {
    return null
  }

  const user = await resolveFreshAuth(context.queryClient)

  if (!user) {
    return null
  }

  return user
}

export const requireAuthBeforeLoad = async ({
  context,
  location,
}: AuthGuardContext) => {
  const user = await getResolvedAuth({ context })

  if (!user) {
    throw redirect({
      to: "/login",
      search: {
        redirect: buildRedirectTarget(location),
      },
    })
  }

  return user
}

export const requireAdminBeforeLoad = async (input: AuthGuardContext) => {
  const user = await requireAuthBeforeLoad(input)

  if (!user.isAdmin) {
    throw redirect({ to: "/unauthorized" })
  }

  return user
}

export const requireGuestBeforeLoad = async ({
  context,
  location,
}: Pick<AuthGuardContext, "context" | "location">): Promise<void> => {
  const user = await getResolvedAuth({ context })

  if (user) {
    const redirectTarget = new URLSearchParams(location.searchStr).get(
      "redirect",
    )

    throw redirect({
      to: sanitizeRedirectTarget(redirectTarget) ?? "/",
    })
  }
}
